const CAUSE_LABELS = {
  none: "Coberto",
  no_composition: "Sem composição",
  remaining_disbursements: "Saldo não distribuído",
  unclassified: "A revisar",
  no_schedule: "Sem planejamento",
  invalid_values: "Dados inconsistentes",
};

const CONFIDENCE_LABELS = {
  high: "Alta confiança",
  medium: "Confiança média",
  low: "Baixa confiança",
};

export const causeLabel = (value) => CAUSE_LABELS[value] ?? "A revisar";
export const confidenceLabel = (value) =>
  CONFIDENCE_LABELS[value] ?? "Confiança indisponível";

const finite = (value) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const formatPercentage = (value) =>
  finite(value) === null
    ? "—"
    : (value * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }) + "%";

const state = ({
  status,
  title,
  description,
  gapValue = null,
  percentage = null,
}) => ({
  visible: true,
  status,
  title,
  description,
  gapValue,
  percentage,
  percentageLabel: formatPercentage(percentage),
  segments: [],
  canOpen: false,
});

const inconsistent = () =>
  state({
    status: "inconsistent",
    title: "Cronograma de desembolso não confiável",
    description:
      "Os valores não reconciliam. Atualize a obra antes de usar este cronograma no fluxo de caixa.",
  });

export const buildCoverageViewModel = (summary) => {
  if (!summary) return { visible: false };
  if (summary.applicable !== true || summary.status === "not_applicable") {
    return { visible: false };
  }
  if (summary.status === "inconsistent") return inconsistent();
  if (summary.available !== true || summary.status === "unavailable") {
    return state({
      status: "unavailable",
      title: "Diagnóstico de desembolso ainda indisponível",
      description:
        "Atualize o desembolso da obra para identificar o que entrou no cronograma e o que precisa de ajuste.",
    });
  }

  const scheduledValue = finite(summary.scheduledValue);
  const distributedValue = finite(summary.distributedValue);
  const gapValue = finite(summary.gapValue);
  const priceInformedValue = finite(summary.priceInformedValue);
  const remainingValue = finite(summary.remainingValue);
  const unclassifiedValue = finite(summary.unclassifiedValue);
  const percentage = finite(summary.percentage);
  if (
    [
      scheduledValue,
      distributedValue,
      gapValue,
      priceInformedValue,
      remainingValue,
      unclassifiedValue,
    ].some((value) => value === null || value < 0) ||
    percentage === null ||
    percentage < 0 ||
    percentage > 1
  ) {
    return inconsistent();
  }
  const tolerance = Math.max(1, Math.abs(scheduledValue) * 0.0001);
  if (
    Math.abs(scheduledValue - distributedValue - gapValue) > tolerance ||
    Math.abs(
      gapValue - priceInformedValue - remainingValue - unclassifiedValue,
    ) > tolerance
  ) {
    return inconsistent();
  }

  const width = (value) =>
    scheduledValue > 0 ? (value / scheduledValue) * 100 : 0;
  const segments = [
    {
      key: "distributed",
      label: "No desembolso",
      value: distributedValue,
      width: width(distributedValue),
    },
    {
      key: "noComposition",
      label: "Sem composição",
      value: priceInformedValue,
      width: width(priceInformedValue),
    },
    {
      key: "remaining",
      label: "Saldo não distribuído",
      value: remainingValue,
      width: width(remainingValue),
    },
    {
      key: "unclassified",
      label: "A revisar",
      value: unclassifiedValue,
      width: width(unclassifiedValue),
    },
  ];
  const complete = summary.status === "complete" || gapValue <= tolerance;
  return {
    visible: true,
    status: complete ? "complete" : "partial",
    title: complete
      ? "Cronograma de desembolso coberto"
      : "Parte do planejamento está fora do desembolso",
    description: complete
      ? "O valor planejado está praticamente todo distribuído em competências."
      : "Veja quais serviços precisam de composição ou revisão das condições de desembolso.",
    scheduledValue,
    distributedValue,
    gapValue,
    priceInformedValue,
    remainingValue,
    unclassifiedValue,
    percentage,
    percentageLabel: formatPercentage(percentage),
    segments,
    counts: summary.counts ?? null,
    confidence: summary.confidence ?? null,
    measurementNumber: summary.measurementNumber ?? null,
    synchronizedAt: summary.synchronizedAt ?? null,
    canOpen: true,
  };
};
